/**
 * Preferences slice.
 *
 * Local, per-project UI preferences and GitHub integration config.
 *
 * Device-local settings persisted through projectPrefs.ts, not part of the OST
 * payload synced to the server.
 *
 * Slices share one store: `set` and `get` are typed over the whole AppStore,
 * so an action here can read or write another slice's state where the domains
 * genuinely interact (loading a project resets the tree, converting a triage
 * item calls the tree's addNode).
 */
import type { StateCreator } from 'zustand';
import type { Quality } from '../../components/ui/QualitySlider';
import { writeProjectPref } from '../projectPrefs';
import { saveGithubTarget } from '../../api/sync';
import type { AppStore } from '../storeTypes';

export const createPreferencesSlice: StateCreator<
    AppStore,
    [],
    [],
    Pick<
        AppStore,
        | 'githubToken'
        | 'githubRepo'
        | 'githubProjectId'
        | 'aiSuggestionCount'
        | 'setGithubProjectId'
        | 'setAiSuggestionCount'
        | 'aiModelQuality'
        | 'setAiModelQuality'
        | 'gridSpacing'
        | 'setGridSpacing'
        | 'snapToGrid'
        | 'setSnapToGrid'
        | 'setGithubToken'
        | 'setGithubRepo'
    >
> = (set, get) => ({
    // The GitHub PAT is deliberately NOT read from localStorage and never written
    // there — setGithubToken keeps it in memory only, per the privacy policy. An
    // older build did persist it under the unscoped 'githubToken' key, so
    // purgeLegacyGithubToken() (below) deletes any such leftover on load rather
    // than leaving a credential sitting in browser storage indefinitely.
    //
    // Consequence: anyone whose token was only in localStorage re-enters it once
    // in Settings. That is the behaviour the policy already promised.
    githubToken: '',

    // NOTE: the remaining module-load defaults read UNSCOPED keys, while every
    // setter writes — and setActiveProject reads — the project-scoped
    // `<key>_<projectId>` form (see projectPrefs.ts). Nothing writes the unscoped
    // keys any more, so these are effectively legacy reads that resolve to their
    // fallbacks; the real values load on setActiveProject. Kept because, unlike a
    // credential, a stale repo name or grid setting is harmless to carry forward.
    githubRepo: localStorage.getItem('githubRepo') || '',
    githubProjectId: localStorage.getItem('githubProjectId') || '',
    aiSuggestionCount: Math.max(1, Math.min(10, parseInt(localStorage.getItem('aiSuggestionCount') || '3', 10))),

    setGithubProjectId: (id: string) => {
        // Persist per-project so GitHub integration is scoped to each project.
        writeProjectPref(get().activeProject?._id, 'githubProjectId', id);
        set({ githubProjectId: id });
        void persistGithubTarget(get().activeProject?._id, { projectV2Id: id });
    },

    setAiSuggestionCount: (n: number) => {
        const clamped = Math.max(1, Math.min(10, Math.round(n)));
        // Persist per-project so AI preferences are scoped to each project.
        writeProjectPref(get().activeProject?._id, 'aiSuggestionCount', String(clamped));
        set({ aiSuggestionCount: clamped });
    },

    // Preferred AI model quality persisted in localStorage for cross-session preference
    aiModelQuality: (localStorage.getItem('aiModelQuality') as Quality) || 'low',

    setAiModelQuality: (q: Quality) => {
        // Persist per-project so AI quality preference is scoped to each project.
        writeProjectPref(get().activeProject?._id, 'aiModelQuality', q);
        set({ aiModelQuality: q });
    },

    // Canvas grid defaults
    gridSpacing: parseInt(localStorage.getItem('gridSpacing') || '15', 10),

    setGridSpacing: (n: number) => {
        const clamped = Math.max(8, Math.round(n));
        writeProjectPref(get().activeProject?._id, 'gridSpacing', String(clamped));
        set({ gridSpacing: clamped });
    },

    snapToGrid: (localStorage.getItem('snapToGrid') ?? '1') === '1',

    setSnapToGrid: (b: boolean) => {
        writeProjectPref(get().activeProject?._id, 'snapToGrid', b ? '1' : '0');
        set({ snapToGrid: b });
    },

    setGithubToken: (t: string) => {
        // GitHub PAT is kept in memory only — not persisted to localStorage per our privacy policy.
        set({ githubToken: t });
    },

    setGithubRepo: (r: string) => {
        // Persist per-project so GitHub repo selection is scoped to each project.
        writeProjectPref(get().activeProject?._id, 'githubRepo', r);
        set({ githubRepo: r });
        // Also record it on the project server-side, so the Plan page's server-run Sync knows
        // where to push without the browser having to tell it. The localStorage write above
        // stays as-is: it is what every existing GitHub feature reads, and it keeps working
        // offline. Fire-and-forget — a failure here only means the first sync from this device
        // has to supply the repo itself, which it already does.
        void persistGithubTarget(get().activeProject?._id, { repoFullName: r });
    },
});

/** Mirrors a GitHub target selection onto the project document; never throws at the caller. */
function persistGithubTarget(projectId: string | undefined, target: { repoFullName?: string; projectV2Id?: string }) {
    if (!projectId) return Promise.resolve();
    return saveGithubTarget(projectId, target).catch(() => undefined);
}
